import { Link } from 'react-router-dom';

interface ArticleLayoutProps {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  category: string;
  readTime: string;
  dateLabel: string;
  schemaData: Record<string, unknown>;
  children: React.ReactNode;
}

export default function ArticleLayout({
  title,
  description,
  image,
  imageAlt,
  category,
  readTime,
  dateLabel,
  schemaData,
  children,
}: ArticleLayoutProps) {
  return (
    <>
      <article className="pt-24 md:pt-28 pb-8">
        <div className="container-page">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-foreground-600">{title}</span>
          </nav>

          <div className="max-w-3xl mx-auto">
            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold">
                  {dateLabel}
                </span>
                <span className="inline-block px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-semibold">
                  {category}
                </span>
                <span className="text-xs text-foreground-400">{readTime}</span>
              </div>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
                {title}
              </h1>

              <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
                {description}
              </p>
            </header>

            <div className="relative w-full h-[300px] md:h-[400px] rounded-2xl overflow-hidden mb-10">
              <img
                src={image}
                alt={imageAlt}
                title={title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="text-foreground-800 leading-relaxed space-y-6">
              {children}
            </div>

            <div className="mt-12 p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200/50">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                Ready to Start Shopping with the Best AllChinaBuy Spreadsheet?
              </h2>
              <p className="text-foreground-700 text-sm leading-relaxed mb-5">
                Access the most updated AllChinaBuy Spreadsheet today and start discovering trusted products across 14 categories with daily refreshes, QC guides, and complete shopping tutorials.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors flex items-center gap-1.5"
                >
                  Get the Spreadsheet
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </a>
                <Link
                  to="/"
                  className="px-6 py-3 bg-background-50 text-foreground-900 border border-background-200 rounded-xl text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors flex items-center gap-1.5"
                >
                  Back to Home
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      <section className="py-12 bg-background-100">
        <div className="container-page">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
              More AllChinaBuy Spreadsheet Resources
            </h2>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/"
                className="px-5 py-3 rounded-xl bg-background-50 border border-background-200/60 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:border-primary-300 transition-all whitespace-nowrap"
              >
                <i className="ri-home-line mr-1.5"></i>
                Spreadsheet Home
              </Link>
              <Link
                to="/articles/shopping-guide-2026"
                className="px-5 py-3 rounded-xl bg-background-50 border border-background-200/60 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:border-primary-300 transition-all whitespace-nowrap"
              >
                <i className="ri-book-open-line mr-1.5"></i>
                Shopping Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                <i className="ri-shopping-cart-line mr-1.5"></i>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${typeof import.meta.env.VITE_SITE_URL === 'string' ? import.meta.env.VITE_SITE_URL : 'https://allchinabuyspreadsheet.ink'}/`,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: title,
              },
            ],
          }),
        }}
      />
    </>
  );
}